import React, { useMemo, useState } from "react";
import { ArrowRightLeft, FileOutput, FolderArchive, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminUser } from "@/lib/roles";
import { toast } from "sonner";

const PROTECTED = new Set(["orders", "supply_chain", "purchase_orders"]);
const dayStamp = () => new Date().toISOString().slice(0, 10).replaceAll("-", "_");
const csvCell = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;

const csvFor = (rows) => {
  const keys = [...new Set(rows.flatMap((row) => Object.keys(row || {}).filter((key) => key !== "_id")))];
  return [keys.join(","), ...rows.map((row) => keys.map((key) => csvCell(typeof row[key] === "object" ? JSON.stringify(row[key]) : row[key])).join(","))].join("\n");
};

async function saveCsv(csv, filename) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  if ("showSaveFilePicker" in window) {
    try {
      const handle = await window.showSaveFilePicker({ suggestedName: filename, types: [{ description: "CSV", accept: { "text/csv": [".csv"] } }] });
      const writable = await handle.createWritable(); await writable.write(blob); await writable.close();
      return true;
    } catch (error) { if (error?.name === "AbortError") return false; }
  }
  const url = URL.createObjectURL(blob); const link = document.createElement("a");
  link.href = url; link.download = filename; link.click(); URL.revokeObjectURL(url);
  return true;
}

export const SelectionCheckbox = ({ checked, onChange, testId, label }) => <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} aria-label={label} className="h-4 w-4 accent-[var(--brand)] cursor-pointer" data-testid={testId} />;

export const SelectAllCheckbox = ({ records, selectedIds, setSelectedIds }) => {
  const all = records.length > 0 && records.every((record) => selectedIds.includes(record.id));
  return <SelectionCheckbox checked={all} onChange={(checked) => setSelectedIds(checked ? records.map((record) => record.id) : [])} testId="select-all-checkbox" label="Select all visible records" />;
};

export const BulkActions = ({ section, records, selectedIds, setSelectedIds, onComplete }) => {
  const { user } = useAuth(); const [working, setWorking] = useState(false);
  const selected = useMemo(() => records.filter((record) => selectedIds.includes(record.id)), [records, selectedIds]);
  const archiveOnly = PROTECTED.has(section); const canMutate = isAdminUser(user);
  if (!selected.length) return null;
  const exportSelected = async (forMutation = false) => {
    const filename = `${dayStamp()}_${forMutation ? "Purge" : "Export"}.csv`;
    const saved = await saveCsv(csvFor(selected), filename);
    if (!saved) return false;
    await api.post("/bulk/audit", { section, action: "export", ids: selected.map((row) => row.id), filename });
    return true;
  };
  const mutate = async () => {
    const action = archiveOnly ? "archive" : "purge";
    setWorking(true);
    try {
      if (!await exportSelected(true)) return;
      const prompt = archiveOnly
        ? `Are you sure you want to archive ${selected.length} selected records? They will be removed from visible dashboards.`
        : `Are you sure you want to purge ${selected.length} selected records? This cannot be undone.`;
      if (!window.confirm(prompt)) return;
      const { data } = await api.post("/bulk/actions", { section, action, ids: selected.map((row) => row.id) });
      toast.success(`${data.count} record${data.count === 1 ? "" : "s"} ${archiveOnly ? "archived" : "purged"}`);
      setSelectedIds([]); await onComplete?.();
    } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Bulk action could not be completed"); }
    finally { setWorking(false); }
  };
  return <div className="fixed bottom-5 left-1/2 z-50 flex w-[min(94vw,860px)] -translate-x-1/2 items-center justify-between gap-3 border border-slate-700/80 bg-slate-950/95 p-3 shadow-2xl backdrop-blur-xl rounded-xl" data-testid="bulk-action-toolbar">
    <div className="flex items-center gap-3 text-sm text-white"><span className="rounded-md bg-white/10 px-2 py-1 font-mono text-xs" data-testid="bulk-selection-count">{selected.length} selected</span><Button variant="ghost" size="sm" className="text-slate-300 hover:text-white" onClick={() => setSelectedIds([])} data-testid="bulk-clear-selection-button"><X size={14} className="mr-1" /> Clear</Button></div>
    <div className="flex items-center gap-2">
      {canMutate && (archiveOnly ? <Button size="sm" className="bg-emerald-700 text-white hover:bg-emerald-800" disabled={working} onClick={mutate} data-testid="bulk-archive-button"><FolderArchive size={14} className="mr-1.5" /> Archive</Button> : <Button size="sm" className="border border-red-800/60 bg-red-950 text-red-100 hover:bg-red-900" disabled={working} onClick={mutate} data-testid="bulk-purge-button"><span>Purge</span><FileOutput size={13} /><ArrowRightLeft size={13} /></Button>)}
    </div>
  </div>;
};